/**
 * Plugin Registry
 *
 * Central registry for all plugins with:
 * - Plugin registration and validation
 * - Lifecycle management (install, enable, disable, uninstall)
 * - Health monitoring
 * - Dependency resolution
 * - Version compatibility checking
 *
 * Follows software addon best practices with:
 * - Semantic versioning
 * - Dependency injection via PluginContext
 * - Isolation (plugins can't access parent app directly)
 * - Resource management
 */

import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/lib/platform/types/database.types';
import { createClient as createBrowserClient } from '@/lib/platform/supabase/client';
import { UniversalAPIClient } from './UniversalAPIClient';
import { PluginLogger } from './PluginLogger';
import { PluginStorage } from './PluginStorage';
import { PluginEventBus } from './PluginEventBus';
import { InMemoryPluginCache } from './PluginCache';

import { pluginLogger } from '@/lib/platform/utils/logger';
import {
  Plugin,
  PluginManifest,
  PluginContext,
  PluginHealth,
  PluginError,
} from '@/lib/platform/types/plugins';

// Create Supabase client - uses service role on server, singleton in browser
function createSupabaseClient() {
  const isServer = typeof window === 'undefined';

  if (isServer && process.env.SUPABASE_SERVICE_ROLE_KEY) {
    // Server-side: use service role to bypass RLS for infrastructure operations
    return createClient<Database>(
      process.env.NEXT_PUBLIC_SUPABASE_URL!,
      process.env.SUPABASE_SERVICE_ROLE_KEY,
      {
        auth: { persistSession: false, autoRefreshToken: false },
      }
    );
  } else {
    // Client-side: use singleton browser client to avoid multiple GoTrueClient instances
    return createBrowserClient();
  }
}

const supabase = createSupabaseClient();

export class PluginRegistry {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private plugins = new Map<string, Plugin<any>>();
  private enabledPlugins = new Map<string, Set<string>>(); // organizationId -> Set<pluginId>
  private healthStatus = new Map<string, PluginHealth>();

  private eventBus: PluginEventBus;
  private cache: InMemoryPluginCache;

  constructor() {
    this.eventBus = new PluginEventBus(false);
    this.cache = new InMemoryPluginCache();
  }

  // ============================================================================
  // Plugin Registration
  // ============================================================================

  /**
   * Register a plugin with validation
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async register(plugin: Plugin<any>): Promise<void> {
    // Validate manifest
    this.validateManifest(plugin.manifest);

    // Check compatibility
    if (!this.isCompatible(plugin.manifest)) {
      throw new PluginError(
        `Plugin ${plugin.manifest.id} is not compatible with system version`,
        plugin.manifest.id,
        'INCOMPATIBLE_VERSION'
      );
    }

    // Check dependencies
    const missingDeps = this.checkDependencies(plugin.manifest);
    if (missingDeps.length > 0) {
      throw new PluginError(
        `Plugin ${plugin.manifest.id} has missing dependencies: ${missingDeps.join(', ')}`,
        plugin.manifest.id,
        'MISSING_DEPENDENCIES'
      );
    }

    // Register plugin
    this.plugins.set(plugin.manifest.id, plugin);

    // Run onInstall hook
    if (plugin.lifecycle.onInstall) {
      try {
        await plugin.lifecycle.onInstall();
      } catch (error) {
        pluginLogger.error(`[PluginRegistry] onInstall failed for ${plugin.manifest.id}:`, error);
        // Don't throw - just log the error
      }
    }

    pluginLogger.log(
      `[PluginRegistry] Registered plugin: ${plugin.manifest.id} v${plugin.manifest.version}`
    );
  }

  /**
   * Unregister a plugin
   */
  async unregister(pluginId: string): Promise<void> {
    const plugin = this.plugins.get(pluginId);
    if (!plugin) {
      return;
    }

    // Disable for all organizations first
    for (const organizationId of this.enabledPlugins.keys()) {
      if (this.isEnabledFor(pluginId, organizationId)) {
        await this.disable(pluginId, organizationId);
      }
    }

    // Run onUninstall hook
    if (plugin.lifecycle.onUninstall) {
      try {
        await plugin.lifecycle.onUninstall();
      } catch (error) {
        pluginLogger.error(`[PluginRegistry] onUninstall failed for ${pluginId}:`, error);
      }
    }

    this.plugins.delete(pluginId);
    pluginLogger.log(`[PluginRegistry] Unregistered plugin: ${pluginId}`);
  }

  // ============================================================================
  // Plugin Lifecycle
  // ============================================================================

  /**
   * Enable a plugin for an organization
   */
  async enable(pluginId: string, organizationId: string): Promise<void> {
    const plugin = this.plugins.get(pluginId);
    if (!plugin) {
      throw new PluginError(`Plugin ${pluginId} not found`, pluginId, 'NOT_FOUND');
    }

    // Check if already enabled
    if (this.isEnabledFor(pluginId, organizationId)) {
      pluginLogger.log(
        `[PluginRegistry] Plugin ${pluginId} already enabled for org ${organizationId}`
      );
      return;
    }

    try {
      // Try to get API key for organization (optional for web UI usage)
      let apiClient: UniversalAPIClient | undefined;
      try {
        const apiKey = await this.getOrganizationAPIKey(organizationId);
        apiClient = new UniversalAPIClient(apiKey, '/api/v1', this.cache);
      } catch {
        pluginLogger.warn(
          `[PluginRegistry] No API key found for org ${organizationId}, plugin will work with limited functionality`
        );
        // Continue without API client - plugins can still use storage, events, etc.
      }

      // Create plugin context with dependency injection
      const context: PluginContext = {
        organizationId,
        apiClient: apiClient, // May be undefined
        cache: this.cache,
        logger: new PluginLogger(pluginId),
        events: this.eventBus,
        storage: new PluginStorage(pluginId, organizationId),
      };

      // Initialize plugin
      await plugin.initialize(context);

      // Run onEnable hook
      if (plugin.lifecycle.onEnable) {
        await plugin.lifecycle.onEnable();
      }

      // Mark as enabled
      if (!this.enabledPlugins.has(organizationId)) {
        this.enabledPlugins.set(organizationId, new Set());
      }
      this.enabledPlugins.get(organizationId)!.add(pluginId);
      plugin.enabled = true;

      // Save to database
      await this.savePluginState(pluginId, organizationId, true, plugin.manifest.version);

      // Emit event
      await this.eventBus.emit({
        type: 'plugin.enabled',
        data: { pluginId, organizationId },
      });

      pluginLogger.log(`[PluginRegistry] Enabled plugin: ${pluginId} for org ${organizationId}`);
    } catch (error: unknown) {
      const errorMessage = (error as Error).message || 'Unknown error';
      throw new PluginError(
        `Failed to enable plugin ${pluginId}: ${errorMessage}`,
        pluginId,
        'ENABLE_FAILED'
      );
    }
  }

  /**
   * Disable a plugin for an organization
   */
  async disable(pluginId: string, organizationId: string): Promise<void> {
    const plugin = this.plugins.get(pluginId);
    if (!plugin) {
      return;
    }

    try {
      // Run onDisable hook
      if (plugin.lifecycle.onDisable) {
        await plugin.lifecycle.onDisable();
      }

      // Cleanup
      await plugin.cleanup();

      // Mark as disabled
      this.enabledPlugins.get(organizationId)?.delete(pluginId);
      plugin.enabled = false;
      plugin.context = undefined;

      // Save to database
      await this.savePluginState(pluginId, organizationId, false, plugin.manifest.version);

      // Emit event
      await this.eventBus.emit({
        type: 'plugin.disabled',
        data: { pluginId, organizationId },
      });

      pluginLogger.log(`[PluginRegistry] Disabled plugin: ${pluginId} for org ${organizationId}`);
    } catch (error: unknown) {
      pluginLogger.error(`[PluginRegistry] Error disabling plugin ${pluginId}:`, error);
      throw error;
    }
  }

  // ============================================================================
  // Health Monitoring
  // ============================================================================

  /**
   * Run health check for a plugin
   */
  async healthCheck(pluginId: string): Promise<PluginHealth> {
    const plugin = this.plugins.get(pluginId);

    if (!plugin || !plugin.lifecycle.healthCheck) {
      return {
        status: 'healthy',
        message: 'No health check available',
        lastCheck: new Date(),
      };
    }

    try {
      const health = await plugin.lifecycle.healthCheck();
      this.healthStatus.set(pluginId, health);

      // Update database
      await this.updateHealthStatus(pluginId, health);

      return health;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const unhealthyStatus: PluginHealth = {
        status: 'unhealthy',
        message: errorMessage,
        lastCheck: new Date(),
      };

      this.healthStatus.set(pluginId, unhealthyStatus);
      await this.updateHealthStatus(pluginId, unhealthyStatus);

      return unhealthyStatus;
    }
  }

  /**
   * Run health checks for all enabled plugins
   */
  async healthCheckAll(): Promise<Map<string, PluginHealth>> {
    const results = new Map<string, PluginHealth>();
    const pluginIds = Array.from(this.plugins.keys());

    for (const pluginId of pluginIds) {
      const health = await this.healthCheck(pluginId);
      results.set(pluginId, health);
    }

    return results;
  }

  // ============================================================================
  // Validation & Compatibility
  // ============================================================================

  private validateManifest(manifest: PluginManifest): void {
    if (!manifest.id || !manifest.version || !manifest.pluginApiVersion) {
      throw new Error(
        'Invalid plugin manifest: missing required fields (id, version, pluginApiVersion)'
      );
    }

    // Validate semantic versioning
    const versionRegex = /^\d+\.\d+\.\d+$/;
    if (!versionRegex.test(manifest.version)) {
      throw new Error('Invalid plugin version: must be semantic version (x.y.z)');
    }
  }

  private isCompatible(manifest: PluginManifest): boolean {
    const systemVersion = process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0';

    if (
      manifest.minSystemVersion &&
      this.compareVersions(systemVersion, manifest.minSystemVersion) < 0
    ) {
      return false;
    }

    if (
      manifest.maxSystemVersion &&
      this.compareVersions(systemVersion, manifest.maxSystemVersion) > 0
    ) {
      return false;
    }

    return true;
  }

  private checkDependencies(manifest: PluginManifest): string[] {
    if (!manifest.dependencies) {
      return [];
    }

    const missing: string[] = [];

    for (const [depId, versionRange] of Object.entries(manifest.dependencies)) {
      const dep = this.plugins.get(depId);

      if (!dep) {
        missing.push(depId);
      } else if (!this.isVersionInRange(dep.manifest.version, versionRange)) {
        missing.push(`${depId} (requires ${versionRange}, found ${dep.manifest.version})`);
      }
    }

    return missing;
  }

  private compareVersions(v1: string, v2: string): number {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);

    for (let i = 0; i < 3; i++) {
      if (parts1[i] > parts2[i]) return 1;
      if (parts1[i] < parts2[i]) return -1;
    }

    return 0;
  }

  private isVersionInRange(version: string, range: string): boolean {
    // Simple implementation - exact match or wildcard
    // Can be enhanced with full semver support
    return version === range || range === '*';
  }

  // ============================================================================
  // Database Operations
  // ============================================================================

  private async getOrganizationAPIKey(organizationId: string): Promise<string> {
    // FIXME: The api_keys table only stores api_key_hash (bcrypt) — the plaintext
    // key cannot be retrieved. The original code selected a non-existent `api_key`
    // column, so this lookup ALWAYS failed and plugins ran without an API client
    // (handled gracefully by the caller). Throwing preserves that behavior instead
    // of handing a useless bcrypt hash to UniversalAPIClient.
    throw new Error(
      `Plaintext API key is not retrievable for organization ${organizationId} (only hashes are stored)`
    );
  }

  private async savePluginState(
    pluginId: string,
    organizationId: string,
    enabled: boolean,
    version: string
  ): Promise<void> {
    const now = new Date().toISOString();

    const { error } = await supabase.from('plugin_settings').upsert(
      {
        organization_id: organizationId,
        plugin_id: pluginId,
        plugin_version: version,
        enabled,
        enabled_at: enabled ? now : null,
        disabled_at: enabled ? null : now,
        updated_at: now,
      },
      {
        onConflict: 'organization_id,plugin_id',
      }
    );

    if (error) {
      pluginLogger.error('[PluginRegistry] Failed to save plugin state:', error);
    } else {
      pluginLogger.log(
        `[PluginRegistry] Saved plugin state: ${pluginId} = ${enabled ? 'enabled' : 'disabled'}`
      );
    }
  }

  private async updateHealthStatus(pluginId: string, health: PluginHealth): Promise<void> {
    // Update all organizations using this plugin
    await supabase
      .from('plugin_settings')
      .update({
        last_health_check: new Date().toISOString(),
        health_status: health.status,
      })
      .eq('plugin_id', pluginId);
  }

  // ============================================================================
  // Query Methods
  // ============================================================================

  isEnabled(pluginId: string): boolean {
    // Check if enabled for ANY organization
    for (const plugins of this.enabledPlugins.values()) {
      if (plugins.has(pluginId)) {
        return true;
      }
    }
    return false;
  }

  isEnabledFor(pluginId: string, organizationId: string): boolean {
    return this.enabledPlugins.get(organizationId)?.has(pluginId) || false;
  }

  getPlugin(pluginId: string): Plugin | undefined {
    return this.plugins.get(pluginId);
  }

  getAllPlugins(): Plugin[] {
    return Array.from(this.plugins.values());
  }

  getEnabledPlugins(organizationId: string): Plugin[] {
    const enabledIds = this.enabledPlugins.get(organizationId);
    if (!enabledIds) {
      return [];
    }

    return Array.from(enabledIds)
      .map((id) => this.plugins.get(id))
      .filter((p): p is Plugin => p !== undefined);
  }

  getReportsPlugins(organizationId: string): Plugin[] {
    return this.getEnabledPlugins(organizationId).filter((p) => p.capabilities.features.reports);
  }

  getHealthStatus(pluginId: string): PluginHealth | undefined {
    return this.healthStatus.get(pluginId);
  }

  getEventBus(): PluginEventBus {
    return this.eventBus;
  }

  getCache(): InMemoryPluginCache {
    return this.cache;
  }

  // ============================================================================
  // Statistics
  // ============================================================================

  getStats(): {
    totalPlugins: number;
    totalOrganizations: number;
    enabledPluginsCount: number;
    healthyPlugins: number;
    degradedPlugins: number;
    unhealthyPlugins: number;
  } {
    let enabledCount = 0;
    for (const plugins of this.enabledPlugins.values()) {
      enabledCount += plugins.size;
    }

    let healthy = 0;
    let degraded = 0;
    let unhealthy = 0;

    for (const health of this.healthStatus.values()) {
      if (health.status === 'healthy') healthy++;
      else if (health.status === 'degraded') degraded++;
      else unhealthy++;
    }

    return {
      totalPlugins: this.plugins.size,
      totalOrganizations: this.enabledPlugins.size,
      enabledPluginsCount: enabledCount,
      healthyPlugins: healthy,
      degradedPlugins: degraded,
      unhealthyPlugins: unhealthy,
    };
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const pluginRegistry = new PluginRegistry();
